import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth, User } from 'firebase/auth';
import { doc, getDoc, updateDoc, Timestamp, collection, getDocs, query, limit } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faSpinner,
  faIdCard,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faCheckCircle,
  faCity,
  faBuilding,
  faInfoCircle,
  faMapMarkerAlt,
  faGlobe,
  faCreditCard,
  faStore,
  faImage,
  faTag,
  faUserCircle,
  faMoneyBill,
  faClock,
  faLink,
  faHashtag,
  faEdit,
  faSave,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { 
  ProfilePageContainer,
  ProfileCard,
  ProfileHeader,
  ProfilePhoto,
  DefaultProfilePhoto,
  ProfileInfo,
  SectionTitle,
  InfoGrid,
  InfoRow,
  Label,
  Value,
  LoadingSpinner,
  StatusMessage,
  ActionButtonContainer,
  Button,
  FormControl,
} from './profile.styles';

// Updated interface to accept userStallNumber
interface ProfileProps {
  userStallNumber: string | null;
}

interface UserProfileData {
  name: string;
  location: string;
  profilePhotoUrl: string;
  stallNumber: string;
  email: string;
  phoneNumber: string;
  createdAt: Date | null;
  status: 'pending' | 'approved' | 'rejected' | string;
  cityTown: string;
  termsAccepted: boolean;
  businessName: string;
  actualLocation: string;
  additionalDetails: string;
  businessPhone: string;
  businessPhotoPreview: string;
  category: string;
  contactPerson: string;
  facebookLink: string;
  googleMapLocation: string;
  instagramLink: string;
  isActive: boolean;
  latitude: number;
  longitude: number;
  paymentStatus: string;
  selectedCountry: string;
  selectedDistrict: string;
  selectedParish: string;
  selectedRegion: string;
  selectedSubcounty: string;
  selectedVillage: string;
  stallType: string;
  subscriptionAmount: number;
  subscriptionDuration: string;
  tiktokLink: string;
  transactionId: string | null;
  userlisting: string;
  website: string;
  whatsappPhone: string;
  xTwitterLink: string;
  linkedinLink: string;
  youtubeLink: string;
  isSuspended: boolean;
}

interface AdminUnit {
  id: string;
  Village: string;
  'Parish/Ward': string;
  'Subcounty/Division': string;
  'District/City': string;
  Region: string;
}

const allCountries = [
  'Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi',
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Cambodia', 'Cameroon', 'Canada',
  'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China',
  'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan',
  'Jordan', 'Kazakhstan', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Thailand',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
  'Turkmenistan', 'Tuvalu', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const Profile: React.FC<ProfileProps> = ({ userStallNumber }) => {
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState<UserProfileData>({
    name: '', location: '', profilePhotoUrl: '', stallNumber: '', email: '',
    phoneNumber: '', createdAt: null, status: '', cityTown: '', termsAccepted: false,
    businessName: '', actualLocation: '', additionalDetails: '', businessPhone: '',
    businessPhotoPreview: '', category: '', contactPerson: '', facebookLink: '',
    googleMapLocation: '', instagramLink: '', isActive: false, latitude: 0,
    longitude: 0, paymentStatus: '', selectedCountry: '', selectedDistrict: '',
    selectedParish: '', selectedRegion: '', selectedSubcounty: '', selectedVillage: '',
    stallType: '', subscriptionAmount: 0, subscriptionDuration: '', tiktokLink: '',
    transactionId: null, userlisting: '', website: '', whatsappPhone: '',
    xTwitterLink: '', linkedinLink: '', isSuspended: false, youtubeLink: '',
  });

  const [updatedData, setUpdatedData] = useState<Partial<UserProfileData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminUnits, setAdminUnits] = useState<AdminUnit[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<string[]>([]);
  const [filteredSubcounties, setFilteredSubcounties] = useState<string[]>([]);
  const [filteredParishes, setFilteredParishes] = useState<string[]>([]);
  const [filteredVillages, setFilteredVillages] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [regions, setRegions] = useState<string[]>([]);

  useEffect(() => {
    const fetchStallProfile = async () => {
      if (!userStallNumber) {
        setError("Stall number not provided.");
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const stallDocRef = doc(db, 'stalls', userStallNumber);
        const stallDocSnap = await getDoc(stallDocRef);

        if (stallDocSnap.exists()) {
          const data = stallDocSnap.data();
          const fetchedData: UserProfileData = {
            name: data.contactPerson || '', location: data.actualLocation || '',
            profilePhotoUrl: data.profilePhotoUrl || '', stallNumber: data.stallNumber || '',
            email: data.email || '', phoneNumber: data.phoneNumber || '',
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
            status: data.status || 'N/A', cityTown: data.cityTown || '',
            termsAccepted: typeof data.termsAccepted === 'boolean' ? data.termsAccepted : false,
            businessName: data.businessName || '', actualLocation: data.actualLocation || '',
            additionalDetails: data.additionalDetails || '', businessPhone: data.businessPhone || '',
            businessPhotoPreview: data.businessPhotoPreview || '', category: data.category || '',
            contactPerson: data.contactPerson || '', facebookLink: data.facebookLink || '',
            googleMapLocation: data.googleMapLocation || '', instagramLink: data.instagramLink || '',
            isActive: data.isActive || false, latitude: data.latitude || 0,
            longitude: data.longitude || 0, paymentStatus: data.paymentStatus || '',
            selectedCountry: data.selectedCountry || '', selectedDistrict: data.selectedDistrict || '',
            selectedParish: data.selectedParish || '', selectedRegion: data.selectedRegion || '',
            selectedSubcounty: data.selectedSubcounty || '', selectedVillage: data.selectedVillage || '',
            stallType: data.stallType || '', subscriptionAmount: data.subscriptionAmount || 0,
            subscriptionDuration: data.subscriptionDuration || '', tiktokLink: data.tiktokLink || '',
            transactionId: data.transactionId || null, userlisting: data.userlisting || '',
            website: data.website || '', whatsappPhone: data.whatsappPhone || '',
            xTwitterLink: data.xTwitterLink || '', linkedinLink: data.linkedinLink || '',
            isSuspended: data.isSuspended || false, youtubeLink: data.youtubeLink || '',
          };
          setProfileData(fetchedData);
          setUpdatedData(fetchedData);
        } else {
          setError(`Stall data not found for ${userStallNumber}.`);
        }
      } catch (err: any) {
        console.error('Error fetching stall profile:', err);
        setError('Failed to load stall data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchAdminUnits = async () => {
      try {
        const adminUnitsQuery = query(collection(db, 'administrativeUnits'), limit(100));
        const querySnapshot = await getDocs(adminUnitsQuery);
        const unitsData = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
        })) as AdminUnit[];
        setAdminUnits(unitsData);
      } catch (error) {
        console.error("Error fetching administrative units:", error);
      }
    };

    fetchStallProfile();
    fetchAdminUnits();
  }, [userStallNumber]);

  // Filter districts based on selected region
  useEffect(() => {
    if (updatedData.selectedRegion) {
      const districts = adminUnits
        .filter(unit => unit.Region === updatedData.selectedRegion)
        .map(unit => unit['District/City'])
        .filter((value, index, self) => self.indexOf(value) === index);
      setFilteredDistricts(districts);
    } else {
      setFilteredDistricts([]);
    }
  }, [updatedData.selectedRegion, adminUnits]);

  // Filter subcounties based on selected district
  useEffect(() => {
    if (updatedData.selectedDistrict) {
      const subcounties = adminUnits
        .filter(unit => unit['District/City'] === updatedData.selectedDistrict)
        .map(unit => unit['Subcounty/Division'])
        .filter((value, index, self) => self.indexOf(value) === index);
      setFilteredSubcounties(subcounties);
    } else {
      setFilteredSubcounties([]);
    }
  }, [updatedData.selectedDistrict, adminUnits]);

  // Filter parishes based on selected subcounty
  useEffect(() => {
    if (updatedData.selectedSubcounty) {
      const parishes = adminUnits
        .filter(unit => unit['Subcounty/Division'] === updatedData.selectedSubcounty)
        .map(unit => unit['Parish/Ward'])
        .filter((value, index, self) => self.indexOf(value) === index);
      setFilteredParishes(parishes);
    } else {
      setFilteredParishes([]);
    }
  }, [updatedData.selectedSubcounty, adminUnits]);

  // Filter villages based on selected parish
  useEffect(() => {
    if (updatedData.selectedParish) {
      const villages = adminUnits
        .filter(unit => unit['Parish/Ward'] === updatedData.selectedParish)
        .map(unit => unit.Village)
        .filter((value, index, self) => self.indexOf(value) === index);
      setFilteredVillages(villages);
    } else {
      setFilteredVillages([]);
    }
  }, [updatedData.selectedParish, adminUnits]);

  // Populate regions based on selected country
  useEffect(() => {
    if (updatedData.selectedCountry === 'Uganda') {
      const regionNames = adminUnits.map(unit => unit.Region).filter((value, index, self) => self.indexOf(value) === index);
      setRegions(regionNames);
    } else {
      setRegions([]);
    }
  }, [updatedData.selectedCountry, adminUnits]);

  // Update services when profileData.additionalDetails changes
  useEffect(() => {
    setServices(profileData.additionalDetails ? profileData.additionalDetails.split(',').map(s => s.trim()) : ['']);
  }, [profileData.additionalDetails]);

  // Update updatedData.additionalDetails when services change
  useEffect(() => {
    const joined = services.filter(s => s.trim()).join(', ');
    setUpdatedData(prev => ({ ...prev, additionalDetails: joined }));
  }, [services]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    // Handle boolean values from checkboxes
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    if (name === 'selectedCountry') {
      setUpdatedData(prev => ({
        ...prev,
        selectedCountry: value,
        selectedRegion: '',
        selectedDistrict: '',
        selectedSubcounty: '',
        selectedParish: '',
        selectedVillage: '',
      }));
    } else if (name === 'selectedRegion') {
      setUpdatedData(prev => ({
        ...prev,
        selectedRegion: value,
        selectedDistrict: '',
        selectedSubcounty: '',
        selectedParish: '',
        selectedVillage: '',
      }));
    } else if (name === 'selectedDistrict') {
      setUpdatedData(prev => ({
        ...prev,
        selectedDistrict: value,
        selectedSubcounty: '',
        selectedParish: '',
        selectedVillage: '',
      }));
    } else if (name === 'selectedSubcounty') {
      setUpdatedData(prev => ({
        ...prev,
        selectedSubcounty: value,
        selectedParish: '',
        selectedVillage: '',
      }));
    } else if (name === 'selectedParish') {
      setUpdatedData(prev => ({
        ...prev,
        selectedParish: value,
        selectedVillage: '',
      }));
    } else {
      setUpdatedData(prev => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleSave = async () => {
    if (!userStallNumber) {
      setError("Stall number is missing. Cannot save changes.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let imageUrl = updatedData.businessPhotoPreview;
      if (selectedPhoto) {
        const filename = selectedPhoto.name;
        const imageRef = ref(storage, `stall-images/${userStallNumber}/${filename}`);
        await uploadBytes(imageRef, selectedPhoto);
        imageUrl = await getDownloadURL(imageRef);
        setUpdatedData(prev => ({ ...prev, businessPhotoPreview: imageUrl }));
      }
      const stallDocRef = doc(db, 'stalls', userStallNumber);
      await updateDoc(stallDocRef, { ...updatedData, businessPhotoPreview: imageUrl });
      setProfileData({ ...updatedData, businessPhotoPreview: imageUrl } as UserProfileData);
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setEditing(false);
      alert('Profile updated successfully!');
    } catch (err) {
      console.error("Error updating document:", err);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUpdatedData(profileData);
    setServices(profileData.additionalDetails ? profileData.additionalDetails.split(',').map(s => s.trim()) : ['']);
    setSelectedPhoto(null);
    setPhotoPreview(null);
    setEditing(false);
  };

  const addService = () => {
    setServices(prev => [...prev, '']);
  };

  const removeService = (index: number) => {
    setServices(prev => prev.filter((_, i) => i !== index));
  };

  const updateService = (index: number, value: string) => {
    setServices(prev => prev.map((service, i) => i === index ? value : service));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  if (loading) {
    return (
      <ProfilePageContainer>
        <LoadingSpinner />
      </ProfilePageContainer>
    );
  }

  if (error) {
    return (
      <ProfilePageContainer>
        <ProfileCard>
          <StatusMessage type="error">{error}</StatusMessage>
        </ProfileCard>
      </ProfilePageContainer>
    );
  }

  return (
    <ProfilePageContainer>
      <ProfileCard>
        <ProfileHeader>
          {editing ? (
            photoPreview || profileData.businessPhotoPreview ? (
              <ProfilePhoto src={photoPreview || profileData.businessPhotoPreview} alt="Stall Profile" />
            ) : (
              <DefaultProfilePhoto>
                <FontAwesomeIcon icon={faUser} size="4x" />
              </DefaultProfilePhoto>
            )
          ) : (
            profileData.businessPhotoPreview ? (
              <ProfilePhoto src={profileData.businessPhotoPreview} alt="Stall Profile" />
            ) : (
              <DefaultProfilePhoto>
                <FontAwesomeIcon icon={faUser} size="4x" />
              </DefaultProfilePhoto>
            )
          )}
          <h2>{profileData.businessName || 'No Business Name'}</h2>
          <p>{profileData.contactPerson || 'N/A'}</p>
        </ProfileHeader>

        <ActionButtonContainer>
          {!editing ? (
            <Button onClick={() => setEditing(true)}>
              <FontAwesomeIcon icon={faEdit} /> Edit Profile
            </Button>
          ) : (
            <>
              <Button onClick={handleSave} disabled={saving}>
                <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button onClick={handleCancel}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </Button>
            </>
          )}
        </ActionButtonContainer>

        <ProfileInfo>
          <SectionTitle>Stall Information</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <span className="icon"><FontAwesomeIcon icon={faStore} /></span>
              <Label>Stall Number:</Label>
              <Value>{profileData.stallNumber || 'N/A'}</Value>
            </InfoRow>
            {editing ? (
              <FormControl>
                <Label htmlFor="businessName">Business Name:</Label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={updatedData.businessName || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faBuilding} /></span>
                <Label>Business Name:</Label>
                <Value>{profileData.businessName || 'N/A'}</Value>
              </InfoRow>
            )}
            {/* Repeat for all other fields to create the editing/display logic */}
            {editing ? (
              <FormControl>
                <Label htmlFor="stallType">Stall Type:</Label>
                <input
                  type="text"
                  id="stallType"
                  name="stallType"
                  value={updatedData.stallType || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faTag} /></span>
                <Label>Stall Type:</Label>
                <Value>{profileData.stallType || 'N/A'}</Value>
              </InfoRow>
            )}
            <InfoRow>
              <span className="icon"><FontAwesomeIcon icon={faStore} /></span>
              <Label>Category:</Label>
              <Value>{profileData.category || 'N/A'}</Value>
            </InfoRow>
            {editing ? (
              <FormControl>
                <Label htmlFor="contactPerson">Contact Person:</Label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  value={updatedData.contactPerson || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faUserCircle} /></span>
                <Label>Contact Person:</Label>
                <Value>{profileData.contactPerson || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="businessPhone">Business Phone:</Label>
                <input
                  type="text"
                  id="businessPhone"
                  name="businessPhone"
                  value={updatedData.businessPhone || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                <Label>Business Phone:</Label>
                <Value>{profileData.businessPhone || 'N/A'}</Value>
              </InfoRow>
            )}
             {editing ? (
              <FormControl>
                <Label htmlFor="businessPhone">Email:</Label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={updatedData.email || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                <Label>Email:</Label>
                <Value>{profileData.email || 'N/A'}</Value>
              </InfoRow>
            )}
          </InfoGrid>

          <SectionTitle>Location Details</SectionTitle>
          <InfoGrid>
            
            {/* {editing ? (
              <FormControl>
                <Label htmlFor="cityTown">City/Town:</Label>
                <select
                  id="cityTown"
                  name="cityTown"
                  value={updatedData.cityTown || ''}
                  onChange={handleChange}
                >
                  <option value="">Select City/Town</option>
                  {adminUnits.map(unit => (
                    <option key={unit.id} value={unit['District/City']}>{unit['District/City']}</option>
                  ))}
                </select>
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faCity} /></span>
                <Label>City/Town:</Label>
                <Value>{profileData.cityTown || 'N/A'}</Value>
              </InfoRow>
            )} */}

             {editing ? (
               <FormControl>
                 <Label htmlFor="selectedCountry">Country:</Label>
                 <select
                   id="selectedCountry"
                   name="selectedCountry"
                   value={updatedData.selectedCountry || ''}
                   onChange={handleChange}
                 >
                   <option value="">Select Country</option>
                   {allCountries.map((countryName: string, index: number) => (
                     <option key={index} value={countryName}>{countryName}</option>
                   ))}
                 </select>
               </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faGlobe} /></span>
                <Label>Country:</Label>
                <Value>{profileData.selectedCountry || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="selectedRegion">Region:</Label>
                <select
                  id="selectedRegion"
                  name="selectedRegion"
                  value={updatedData.selectedRegion || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Region</option>
                  {regions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>Region:</Label>
                <Value>{profileData.selectedRegion || 'N/A'}</Value>
              </InfoRow>
            )}
             {editing ? (
               <FormControl>
                 <Label htmlFor="selectedDistrict">District:</Label>
                 <select
                   id="selectedDistrict"
                   name="selectedDistrict"
                   value={updatedData.selectedDistrict || ''}
                   onChange={handleChange}
                 >
                   <option value="">Select District</option>
                   {filteredDistricts.map(district => (
                     <option key={district} value={district}>{district}</option>
                   ))}
                 </select>
               </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>District:</Label>
                <Value>{profileData.selectedDistrict || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="selectedSubcounty">Subcounty:</Label>
                <select
                  id="selectedSubcounty"
                  name="selectedSubcounty"
                  value={updatedData.selectedSubcounty || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Subcounty</option>
                  {filteredSubcounties.map(subcounty => (
                    <option key={subcounty} value={subcounty}>{subcounty}</option>
                  ))}
                </select>
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>Subcounty:</Label>
                <Value>{profileData.selectedSubcounty || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="selectedParish">Parish:</Label>
                <select
                  id="selectedParish"
                  name="selectedParish"
                  value={updatedData.selectedParish || ''}
                  onChange={handleChange}
                >
                  <option value="">Select Parish</option>
                  {filteredParishes.map(parish => (
                    <option key={parish} value={parish}>{parish}</option>
                  ))}
                </select>
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>Parish:</Label>
                <Value>{profileData.selectedParish || 'N/A'}</Value>
              </InfoRow>
            )}

             {/* {editing ? (
              <>
                <FormControl>
                  <Label htmlFor="latitude">Latitude:</Label>
                  <input
                    type="number"
                    step="0.000001"
                    id="latitude"
                    name="latitude"
                    value={updatedData.latitude || 0}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl>
                  <Label htmlFor="longitude">Longitude:</Label>
                  <input
                    type="number"
                    step="0.000001"
                    id="longitude"
                    name="longitude"
                    value={updatedData.longitude || 0}
                    onChange={handleChange}
                  />
                </FormControl>
              </>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>Coordinates:</Label>
                <Value>
                  {profileData.latitude && profileData.longitude
                    ? `${profileData.latitude.toFixed(6)}, ${profileData.longitude.toFixed(6)}`
                    : 'N/A'}
                </Value>
              </InfoRow>
            )} */}
            {editing ? (
              <FormControl>
                <Label htmlFor="actualLocation">Village/Actual Location of Business:</Label>
                <input
                  type="text"
                  id="actualLocation"
                  name="actualLocation"
                  value={updatedData.actualLocation || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                <Label>Village/Actual Location of Business:</Label>
                <Value>{profileData.actualLocation || 'N/A'}</Value>
              </InfoRow>
            )}
          </InfoGrid>

          <SectionTitle>Subscription & Status</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <span className="icon"><FontAwesomeIcon icon={faMoneyBill} /></span>
              <Label>Subscription Amount:</Label>
              <Value>
                {profileData.subscriptionAmount
                  ? `UGX${profileData.subscriptionAmount}`
                  : 'N/A'}
              </Value>
            </InfoRow>
            <InfoRow>
              <span className="icon"><FontAwesomeIcon icon={faClock} /></span>
              <Label>Subscription Duration:</Label>
              <Value>{profileData.subscriptionDuration || 'N/A'}</Value>
            </InfoRow>
            {editing ? (
              <FormControl>
                <Label htmlFor="paymentStatus">Payment Status:</Label>
                <input
                  type="text"
                  id="paymentStatus"
                  name="paymentStatus"
                  value={updatedData.paymentStatus || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faCreditCard} /></span>
                <Label>Payment Status:</Label>
                <Value>{profileData.paymentStatus || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="isActive">Active:</Label>
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={!!updatedData.isActive}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faCheckCircle} /></span>
                <Label>Active:</Label>
                <Value>{profileData.isActive ? 'Yes' : 'No'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="isSuspended">Suspended:</Label>
                <input
                  type="checkbox"
                  id="isSuspended"
                  name="isSuspended"
                  checked={!!updatedData.isSuspended}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                <Label>Suspended:</Label>
                <Value>{profileData.isSuspended ? 'Yes' : 'No'}</Value>
              </InfoRow>
            )}
          </InfoGrid>

          <SectionTitle>Social Media & Links</SectionTitle>
          <InfoGrid>
             {editing ? (
              <FormControl>
                <Label htmlFor="website">Website:</Label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={updatedData.website || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faGlobe} /></span>
                <Label>Website:</Label>
                <Value>{profileData.website || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="facebookLink">Facebook:</Label>
                <input
                  type="text"
                  id="facebookLink"
                  name="facebookLink"
                  value={updatedData.facebookLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>Facebook:</Label>
                <Value>{profileData.facebookLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="instagramLink">Instagram:</Label>
                <input
                  type="text"
                  id="instagramLink"
                  name="instagramLink"
                  value={updatedData.instagramLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>Instagram:</Label>
                <Value>{profileData.instagramLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="xTwitterLink">Twitter/X:</Label>
                <input
                  type="text"
                  id="xTwitterLink"
                  name="xTwitterLink"
                  value={updatedData.xTwitterLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>Twitter/X:</Label>
                <Value>{profileData.xTwitterLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="linkedinLink">LinkedIn:</Label>
                <input
                  type="text"
                  id="linkedinLink"
                  name="linkedinLink"
                  value={updatedData.linkedinLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>LinkedIn:</Label>
                <Value>{profileData.linkedinLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="tiktokLink">TikTok:</Label>
                <input
                  type="text"
                  id="tiktokLink"
                  name="tiktokLink"
                  value={updatedData.tiktokLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>TikTok:</Label>
                <Value>{profileData.tiktokLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="youtubeLink">YouTube:</Label>
                <input
                  type="text"
                  id="youtubeLink"
                  name="youtubeLink"
                  value={updatedData.youtubeLink || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faLink} /></span>
                <Label>YouTube:</Label>
                <Value>{profileData.youtubeLink || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="whatsappPhone">WhatsApp:</Label>
                <input
                  type="text"
                  id="whatsappPhone"
                  name="whatsappPhone"
                  value={updatedData.whatsappPhone || ''}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faPhone} /></span>
                <Label>WhatsApp:</Label>
                <Value>{profileData.whatsappPhone || 'N/A'}</Value>
              </InfoRow>
            )}
          </InfoGrid>

          <SectionTitle>Additional Information</SectionTitle>
          <InfoGrid>
            {editing ? (
              <FormControl>
                <Label>Services Offered:</Label>
                {services.map((service, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={service}
                      onChange={(e) => updateService(index, e.target.value)}
                      placeholder="Enter a service"
                      style={{ flex: 1, marginRight: '10px' }}
                    />
                    <button type="button" onClick={() => removeService(index)} disabled={services.length === 1}>Remove</button>
                  </div>
                ))}
                <button type="button" onClick={addService}>Add Another Service</button>
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faInfoCircle} /></span>
                <Label>Details:</Label>
                <Value>{profileData.additionalDetails || 'N/A'}</Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="businessPhoto">Business Photo:</Label>
                <input
                  type="file"
                  id="businessPhoto"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{ width: '100px', height: '100px', objectFit: 'cover', marginTop: '10px' }}
                  />
                )}
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faImage} /></span>
                <Label>Business Photo:</Label>
                <Value>
                  {profileData.businessPhotoPreview ? (
                    <img
                      src={profileData.businessPhotoPreview}
                      alt="Business"
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                    />
                  ) : (
                    'N/A'
                  )}
                </Value>
              </InfoRow>
            )}
            {editing ? (
              <FormControl>
                <Label htmlFor="termsAccepted">Terms Accepted:</Label>
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={!!updatedData.termsAccepted}
                  onChange={handleChange}
                />
              </FormControl>
            ) : (
              <InfoRow>
                <span className="icon"><FontAwesomeIcon icon={faCheckCircle} /></span>
                <Label>Terms Accepted:</Label>
                <Value>{profileData.termsAccepted ? 'Yes' : 'No'}</Value>
              </InfoRow>
            )}
          </InfoGrid>
        </ProfileInfo>
      </ProfileCard>
    </ProfilePageContainer>
  );
};

export default Profile;